import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tab } from '@headlessui/react';
import {
  ArrowUpTrayIcon,
  CpuChipIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { craApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, StatusBadge, LoadingSpinner, ModuleIntro } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import {
  CradAssessmentAreasTab,
  CradAaDemographicsTab,
  CradLoanVolumeTab,
  AaConcentrationTable,
  GeographicDistTable,
  GdBpdSummaryTable,
} from '../components/cra/CradSections';

const RATING_BADGE: Record<string, BadgeVariant> = {
  Outstanding: 'success',
  Satisfactory: 'info',
  'Needs to Improve': 'warning',
  'Substantial Noncompliance': 'danger',
};

const CONCLUSION_BADGE: Record<string, BadgeVariant> = {
  'More than Reasonable': 'success',
  Excellent: 'success',
  'A Substantial Majority': 'success',
  Reasonable: 'info',
  'Majority Inside': 'info',
  'Less than Reasonable': 'warning',
  Poor: 'warning',
  'Majority Outside': 'warning',
  Unreasonable: 'danger',
  'Very Poor': 'danger',
  'Substantial Majority Outside': 'danger',
  None: 'neutral',
  'Not Applicable': 'neutral',
  // Wallace, May 2026 — peer data missing for the bank's peer group;
  // analyst must add narrative context. Rendered as neutral so the
  // examiner sees it as "needs attention" rather than pass/fail.
  // Label updated 2026-05-09 per Michael's wording preference.
  'Peer Comparison Unavailable': 'neutral',
};

const TABS = [
  'Overview',
  'Institution Ratings',
  'Assessment Areas',
  'AA Demographics',
  'Loan Volume',
  'LTD Ratio',
  'AA Concentration',
  'GD & BPD Summary',
  'SB Geographic Dist',
  'SB Borrower Profile',
  'CRA Complaints',
];

export default function CradPage() {
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: cradData, isLoading } = useQuery({
    queryKey: ['crad', teamId, id],
    queryFn: () => craApi.getCrad(teamId!, id!),
    enabled: !!teamId && !!id,
  });
  const crad = cradData?.data;

  const computeMutation = useMutation({
    mutationFn: () => craApi.computeCrad(teamId!, id!),
    onSuccess: () => {
      toast.success('CRAD computed successfully');
      queryClient.invalidateQueries({ queryKey: ['crad'] });
    },
    onError: () => toast.error('Failed to compute CRAD'),
  });

  const memorandumMutation = useMutation({
    mutationFn: () => craApi.generateMemorandum(teamId!, id!),
    onSuccess: () => {
      toast.success('CRAD memorandum generated');
      queryClient.invalidateQueries({ queryKey: ['crad'] });
    },
    onError: () => toast.error('Failed to generate memorandum'),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => craApi.importSbLoans(teamId!, file),
    onSuccess: (res) => {
      toast.success(`Imported ${res.data.totalImported} SB loans`);
      queryClient.invalidateQueries({ queryKey: ['crad'] });
    },
    onError: () => toast.error('Failed to import SB loans'),
  });

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) importMutation.mutate(file);
    e.target.value = '';
  }

  function getResultsForCriterion(criterion: string) {
    return (crad?.results || []).filter((r: any) => r.criterion === criterion);
  }

  if (!teamId || !id) return <LoadingSpinner size="lg" />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!crad) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-500">CRAD report not found.</p>
      </div>
    );
  }

  return (
    <FeatureGate feature="module_cra">
      <div className="space-y-6">
        <PageHeader
          title={`CRAD — ${crad.bankName}`}
          description={`${crad.bankCategory} Bank · ${crad.primaryRegulator} · ${crad.evaluationPeriod}`}
          actions={
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleFileSelect} />
              {/* Frictionless clarity: a hover tooltip tells the user EXACTLY which file this expects,
                  so they don't mistakenly upload their ALERT trial-balance file and trust the wrong output. */}
              <div className="group relative">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importMutation.isPending}
                  title="Upload your CRA small-business / small-farm loan register (.xlsx or .csv). This is a dedicated loan file — not your ALERT trial-balance upload."
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowUpTrayIcon className="h-4 w-4" />
                  {importMutation.isPending ? 'Importing...' : 'Import SB Loans'}
                </button>
                <div className="pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-72 rounded-lg border border-gray-200 bg-white p-3 text-left text-xs leading-relaxed text-gray-600 shadow-lg group-hover:block">
                  Upload your <span className="font-medium text-gray-900">CRA small-business / small-farm loan register</span>{' '}
                  (.xlsx or .csv — CRA file-spec or WCC-enriched format).
                  <span className="mt-1 block text-gray-500">
                    This is a separate loan file, not your ALERT trial-balance upload. It carries the gross-annual-revenue
                    data the borrower-profile test needs.
                  </span>
                </div>
              </div>
              <button
                onClick={() => computeMutation.mutate()}
                disabled={computeMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                <CpuChipIcon className="h-4 w-4" />
                {computeMutation.isPending ? 'Computing...' : 'Compute CRAD'}
              </button>
              {crad?.status !== 'DRAFT' && (
                <button
                  onClick={() => memorandumMutation.mutate()}
                  disabled={memorandumMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  {memorandumMutation.isPending ? 'Generating...' : 'Generate Memorandum'}
                </button>
              )}
            </div>
          }
        />

        <ModuleIntro storageKey="crad-report" title="What is the CRAD report?">
          <p>
            The CRAD compiles your bank's CRA Performance Evaluation the way regulators present it:
            the Loan-to-Deposit ratio, lending inside vs. outside your assessment areas, and the geographic
            &amp; borrower distribution of your lending, each with a preliminary conclusion. Click
            <span className="font-medium"> Compute CRAD</span> to (re)run every criterion. No rating is
            shown until every criterion has a result.
          </p>
          <ModuleIntro.Points
            items={[
              <>
                <span className="font-medium text-gray-900">Loan-to-Deposit ratio:</span> pulled automatically
                from your FDIC Call Reports — it needs your FDIC certificate number and last CRA exam date.
              </>,
              <>
                <span className="font-medium text-gray-900">AA Concentration &amp; Small-Business tests:</span>{' '}
                from your small-business / small-farm loan register (the <span className="font-medium">Import SB Loans</span> button).
              </>,
              <>
                <span className="font-medium text-gray-900">Geographic &amp; Borrower distribution:</span> from the
                same small-business / small-farm loan register. HMDA and consumer lending tests are on the
                Lending Test page.
              </>,
            ]}
          />
        </ModuleIntro>

        {/* Overall Rating Card — always shown, so an incomplete CRAD says so instead of vanishing
            or reading as a clean rating (2026-10-15). */}
        <div className="rounded-lg border bg-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Preliminary CRA Rating</p>
              <div className="mt-2">
                {crad.overallRating ? (
                  <StatusBadge variant={RATING_BADGE[crad.overallRating] || 'neutral'}>
                    {crad.overallRating}
                  </StatusBadge>
                ) : (
                  <StatusBadge variant="neutral">Not rated yet</StatusBadge>
                )}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-4 text-center">
              {[
                { label: 'LTD Ratio', value: crad.ltdRatioConclusion },
                { label: 'AA Conc.', value: crad.aaConcentrationConclusion },
                { label: 'Geographic', value: crad.geographicDistConclusion },
                { label: 'Borrower', value: crad.borrowerProfileConclusion },
                { label: 'Complaints', value: crad.complaintsConclusion },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-gray-400">{label}</p>
                  {value ? (
                    <StatusBadge variant={CONCLUSION_BADGE[value] || 'neutral'}>
                      {value}
                    </StatusBadge>
                  ) : (
                    <span className="text-xs text-gray-400">Not computed</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {!crad.overallRating && (
            <div className="mt-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <p className="font-medium">
                {crad.status === 'DRAFT'
                  ? 'Not computed yet. Click Compute CRAD once the data below is in place.'
                  : 'No rating until every Lending Test criterion has a result. Still needed:'}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {missingCriteria(crad).map((m) => (
                  <li key={m.label}>
                    <span className="font-medium">{m.label}:</span> {m.need}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Tabbed Results */}
        <Tab.Group>
          <Tab.List className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-px">
            {TABS.map((tab) => (
              <Tab
                key={tab}
                className={({ selected }) =>
                  clsx(
                    'whitespace-nowrap border-b-2 px-3 py-2 text-xs font-medium transition-colors focus:outline-none',
                    selected
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  )
                }
              >
                {tab}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            {/* Overview */}
            <Tab.Panel>
              <div className="space-y-4">
                <div className="rounded-lg border bg-white p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-navy-900">Overview</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Bank:</span> <span className="font-medium">{crad.bankName}</span></div>
                    <div><span className="text-gray-500">FDIC Cert:</span> <span className="font-medium">{crad.fdicCertNumber || '—'}</span></div>
                    <div><span className="text-gray-500">Bank Size:</span> <span className="font-medium">{crad.bankCategory}</span></div>
                    <div><span className="text-gray-500">Regulator:</span> <span className="font-medium">{crad.primaryRegulator}</span></div>
                    <div><span className="text-gray-500">Evaluation Period:</span> <span className="font-medium">{crad.evaluationPeriod}</span></div>
                    <div><span className="text-gray-500">Products:</span> <span className="font-medium">{crad.productTypes?.join(', ')}</span></div>
                    <div><span className="text-gray-500">Status:</span> <StatusBadge variant={crad.status === 'COMPUTED' ? 'success' : 'info'}>{crad.status}</StatusBadge></div>
                  </div>
                </div>

                {crad.memorandumText && (
                  <div className="rounded-lg border bg-white p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-navy-900">CRAD Memorandum</h3>
                      <span className="text-xs text-gray-400">
                        Generated {crad.memorandumGeneratedAt ? new Date(crad.memorandumGeneratedAt).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {crad.memorandumText}
                    </div>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* Institution Ratings */}
            <Tab.Panel>
              <CriterionTable crad={crad} />
            </Tab.Panel>

            {/* Assessment Areas */}
            <Tab.Panel>
              <CradAssessmentAreasTab teamId={teamId} cradId={id} />
            </Tab.Panel>

            {/* AA Demographics */}
            <Tab.Panel>
              <CradAaDemographicsTab teamId={teamId} cradId={id} />
            </Tab.Panel>

            {/* Loan Volume */}
            <Tab.Panel>
              <CradLoanVolumeTab results={crad.results} />
            </Tab.Panel>

            {/* LTD Ratio */}
            <Tab.Panel>
              <ResultSection title="Loan-to-Deposit Ratio" results={getResultsForCriterion('LTD_RATIO')} showBankValue showComparator
                emptyHint="No Loan-to-Deposit data yet. The LTD ratio is pulled from FDIC Call Reports — it needs the bank's FDIC certificate number and last CRA exam date set on the CRA Dashboard, then recompute the CRAD." />
            </Tab.Panel>

            {/* AA Concentration */}
            <Tab.Panel>
              <ResultSection title="Lending Inside and Outside of Assessment Areas" results={getResultsForCriterion('AA_CONCENTRATION')} showBankValue />
            </Tab.Panel>

            {/* GD & BPD Summary */}
            <Tab.Panel>
              <GdBpdSummaryTable crad={crad} badge={CONCLUSION_BADGE} />
            </Tab.Panel>

            {/* SB Geographic Dist */}
            <Tab.Panel>
              <ResultSection title="Distribution of Loans Inside Assessment Area by Geography" results={getResultsForCriterion('GEOGRAPHIC_DIST')} showBankValue showComparator />
            </Tab.Panel>

            {/* Borrower Profile — handles both SB and SF (Wallace 2026-05-07) */}
            <Tab.Panel>
              <ResultSection title="Distribution of Loans Inside Assessment Area by Borrower Revenue and Loan Amount" results={getResultsForCriterion('BORROWER_PROFILE')} showBankValue showComparator />
            </Tab.Panel>

            {/* CRA Complaints */}
            <Tab.Panel>
              <ResultSection title="CRA-Related Complaints" results={getResultsForCriterion('COMPLAINTS')} showBankValue />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </FeatureGate>
  );
}

// ── Sub-Components ──────────────────────────────────────────────────────────

/** What each missing Lending Test criterion needs before the CRAD can be rated. */
function missingCriteria(crad: any): { label: string; need: string }[] {
  const sbNeed = 'small-business / small-farm loans inside your assessment areas (Import SB Loans), then Compute CRAD.';
  const out: { label: string; need: string }[] = [];
  if (!crad.ltdRatioConclusion) {
    out.push({
      label: 'Loan-to-Deposit Ratio',
      need: crad.fdicCertNumber
        ? 'Call Report data since the last CRA exam: check the last CRA exam date on the CRA Dashboard, then Compute CRAD.'
        : "the bank's FDIC certificate number and last CRA exam date on the CRA Dashboard, then Compute CRAD.",
    });
  }
  if (!crad.aaConcentrationConclusion) out.push({ label: 'Assessment Area Concentration', need: sbNeed });
  if (!crad.geographicDistConclusion) out.push({ label: 'Geographic Distribution', need: sbNeed });
  if (!crad.borrowerProfileConclusion) out.push({ label: "Borrower's Profile Distribution", need: sbNeed });
  return out;
}

function CriterionTable({ crad }: { crad: any }) {
  const criteria = [
    { key: 'ltdRatioConclusion', label: 'Loan-to-Deposit Ratio' },
    { key: 'aaConcentrationConclusion', label: 'Assessment Area Concentration' },
    { key: 'geographicDistConclusion', label: 'Geographic Distribution' },
    { key: 'borrowerProfileConclusion', label: "Borrower's Profile Distribution" },
    { key: 'complaintsConclusion', label: 'CRA-related Complaints' },
  ];

  return (
    <div className="rounded-lg border bg-white p-6">
      <h3 className="text-sm font-semibold text-navy-900 mb-4">Preliminary Ratings — Lending Test</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Lending Test Criterion</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Conclusion</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Related CRA Rating</th>
          </tr>
        </thead>
        <tbody>
          {criteria.map(({ key, label }) => {
            const conclusion = crad[key];
            const ratingMap: Record<string, string> = {
              'More than Reasonable': 'Outstanding',
              'A Substantial Majority': 'Outstanding',
              Excellent: 'Outstanding',
              Reasonable: 'Satisfactory',
              'Majority Inside': 'Satisfactory',
              'Less than Reasonable': 'Needs to Improve',
              Poor: 'Needs to Improve',
              'Majority Outside': 'Needs to Improve',
              None: 'Not Applicable',
            };
            const rating = conclusion ? ratingMap[conclusion] || conclusion : '—';

            return (
              <tr key={key} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-navy-900">{label}</td>
                <td className="px-3 py-2">
                  {conclusion ? <StatusBadge variant={CONCLUSION_BADGE[conclusion] || 'neutral'}>{conclusion}</StatusBadge> : '—'}
                </td>
                <td className="px-3 py-2">
                  {rating !== '—' ? <StatusBadge variant={RATING_BADGE[rating] || 'neutral'}>{rating}</StatusBadge> : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ResultSection({ title, results, showBankValue, showComparator, emptyHint }: {
  title: string;
  results: any[];
  showBankValue?: boolean;
  showComparator?: boolean;
  emptyHint?: string;
}) {
  if (!results?.length) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-sm font-semibold text-navy-900 mb-4">{title}</h3>
        <p className="text-sm text-gray-400">
          {emptyHint ?? 'No data available. Import SB loans and compute the CRAD to populate this tab.'}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-white p-6">
      <h3 className="text-sm font-semibold text-navy-900 mb-4">{title}</h3>
      <div className="space-y-4">
        {results.map((r: any, i: number) => (
          <div key={i} className="rounded-lg border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-navy-900">
                  {r.assessmentAreaName}
                  {r.dataYear > 0 && ` — ${r.dataYear}`}
                </span>
                <StatusBadge variant="info">{r.productType}</StatusBadge>
              </div>
              <div className="flex items-center gap-2">
                {r.comparatorValue?.screeningThreshold && (
                  <span
                    title={`No D&B or CRA aggregate comparator: this conclusion uses a fixed screening threshold (${r.comparatorValue.screeningThreshold}), not a benchmark.`}
                    className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                  >
                    Preliminary: no comparator
                  </span>
                )}
                {r.conclusion && (
                  <StatusBadge variant={CONCLUSION_BADGE[r.conclusion] || 'neutral'}>
                    {r.conclusion}
                  </StatusBadge>
                )}
              </div>
            </div>
            {/* Specialized LTD Ratio renderer (Wallace, May 2026) — the
                generic JSON dump is opaque; the analyst needs to see the
                actual percentage + window + peer comparison at a glance. */}
            {r.criterion === 'LTD_RATIO' && r.bankValue ? (
              <LtdRatioCard bank={r.bankValue} peer={r.comparatorValue} />
            ) : r.criterion === 'BORROWER_PROFILE' && r.bankValue ? (
              <BpdTable bank={r.bankValue} comparator={r.comparatorValue} productType={r.productType} />
            ) : r.criterion === 'AA_CONCENTRATION' && r.bankValue ? (
              <AaConcentrationTable bank={r.bankValue} />
            ) : r.criterion === 'GEOGRAPHIC_DIST' && r.bankValue ? (
              <GeographicDistTable bank={r.bankValue} comparator={r.comparatorValue} />
            ) : (
              <>
                {showBankValue && r.bankValue && (
                  <div className="mt-2 bg-gray-50 rounded p-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Bank Data</p>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(r.bankValue, null, 2)}
                    </pre>
                  </div>
                )}
                {showComparator && r.comparatorValue && (
                  <div className="mt-2 bg-blue-50 rounded p-3">
                    <p className="text-xs font-medium text-blue-600 mb-1">Comparator Data (D&B / Aggregate)</p>
                    <pre className="text-xs text-blue-700 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(r.comparatorValue, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}
            {r.narrative && (
              <p className="mt-2 text-sm text-gray-600">{r.narrative}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LTD Ratio renderer ─────────────────────────────────────────────────────
//
// Renders the regulator-facing "quarterly average net loan-to-deposit ratio"
// alongside the peer-group average + the per-quarter table the average was
// computed from. Built for Michael Wallace's spec (May 2026): result shown
// to 1 decimal place with the description label inline.

interface LtdBankValue {
  averageNetLoanToDepositRatioPct?: number;
  quarterCount?: number;
  windowStart?: string;
  windowEnd?: string;
  descriptionLabel?: string;
  quarters?: Array<{
    date: string;
    netLoans: number | null;
    totalDeposits: number | null;
    ltdPct: number;
  }>;
}

interface LtdPeerValue {
  peerGroupKey?: string;
  peerAvgPct?: number;
  quarterCount?: number;
}

function LtdRatioCard({ bank, peer }: { bank: LtdBankValue; peer: LtdPeerValue | null }) {
  const label = bank.descriptionLabel ?? 'quarterly average net loan-to-deposit ratio';
  const bankPct = bank.averageNetLoanToDepositRatioPct;

  return (
    <div className="mt-3 space-y-3">
      {/* Headline numbers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded p-3">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Bank — {label}</p>
          <p className="text-2xl font-bold text-navy-900 tabular-nums mt-1">
            {bankPct != null ? `${bankPct.toFixed(1)}%` : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {bank.quarterCount ?? 0} quarter{bank.quarterCount === 1 ? '' : 's'}
            {bank.windowStart && bank.windowEnd && ` · ${bank.windowStart} → ${bank.windowEnd}`}
          </p>
        </div>
        <div className="bg-blue-50 rounded p-3">
          <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Peer group — same window</p>
          <p className="text-2xl font-bold text-blue-900 tabular-nums mt-1">
            {peer?.peerAvgPct != null ? `${peer.peerAvgPct.toFixed(1)}%` : '—'}
          </p>
          <p className="text-xs text-blue-700 mt-0.5">
            {peer?.peerGroupKey ?? 'No peer comparison available — analyst must add narrative context'}
          </p>
        </div>
      </div>

      {/* Per-quarter table */}
      {bank.quarters && bank.quarters.length > 0 && (
        <details className="border border-gray-200 rounded">
          <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
            Quarter-by-quarter breakdown ({bank.quarters.length} quarters)
          </summary>
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-y border-gray-200 text-gray-600">
              <tr>
                <th className="text-left px-3 py-1.5 font-semibold">Quarter</th>
                <th className="text-right px-3 py-1.5 font-semibold">Net Loans &amp; Leases</th>
                <th className="text-right px-3 py-1.5 font-semibold">Total Deposits</th>
                <th className="text-right px-3 py-1.5 font-semibold">LTD %</th>
              </tr>
            </thead>
            <tbody>
              {bank.quarters.map((q) => (
                <tr key={q.date} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-1.5 font-mono">{q.date}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {q.netLoans != null ? `$${q.netLoans.toLocaleString()}K` : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {q.totalDeposits != null ? `$${q.totalDeposits.toLocaleString()}K` : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                    {q.ltdPct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

// ─── BPD Table renderer (Wallace, May 7 2026) ───────────────────────────────
//
// SB and SF Borrower Profile Distribution tables differ only in the top
// loan-amount tier ($250–$1000 for SB, $250–$500 for SF). Bank-side
// numbers are computed identically (GAR ≤$1M is the small-business /
// small-farm definition for BOTH products). Aggregate row label flips
// between "Small Business Aggregate" and "Small Farm Aggregate".

interface BpdBankValue {
  garLessThan1M?: number;
  garGreaterThan1M?: number;
  garLessThan1MPct?: number;
  total?: number;
  tierKeys?: string[];
  topTierLabel?: string;
  byGarAndAmount?: Record<string, Record<string, number>>;
}

interface BpdComparatorValue {
  dnb?: { garLessThan1MPct?: number | null } | null;
  aggregate?: { garLessThan1MPct?: number | null } | null;
}

function BpdTable({ bank, comparator, productType }: {
  bank: BpdBankValue;
  comparator: BpdComparatorValue | null;
  productType?: string;
}) {
  const isSmallFarm = productType === 'SMALL_FARM';
  const productLabel = isSmallFarm ? 'Small Farm' : 'Small Business';
  const aggregateLabel = isSmallFarm ? 'Small Farm Aggregate' : 'Small Business Aggregate';

  // Tier keys default for backwards compat with rows persisted before
  // 2026-05-07; if missing, fall back to the SB schema.
  const tierKeys = bank.tierKeys ?? ['<$100', '$100-$250', isSmallFarm ? '$250-$500' : '$250-$1000'];

  const total = bank.total ?? 0;
  const byGar = bank.byGarAndAmount ?? {};
  const garLessThan1MRow = byGar['1'] ?? Object.fromEntries(tierKeys.map(k => [k, 0]));
  const garGreaterRow = byGar['2'] ?? Object.fromEntries(tierKeys.map(k => [k, 0]));

  function rowSum(row: Record<string, number>) {
    return tierKeys.reduce((s, k) => s + (row[k] ?? 0), 0);
  }
  function pct(n: number) { return total > 0 ? (n / total) : 0; }
  function fmt(n: number) { return total > 0 ? `${(n * 100).toFixed(1)}%` : '—'; }

  const tierTotals = tierKeys.map(k => (garLessThan1MRow[k] ?? 0) + (garGreaterRow[k] ?? 0));
  const grandTotal = tierTotals.reduce((s, n) => s + n, 0);

  const aggGar = comparator?.aggregate?.garLessThan1MPct ?? null;
  const dnbGar = comparator?.dnb?.garLessThan1MPct ?? null;

  return (
    <div className="mt-3 space-y-3">
      {/* Headline: GAR ≤$1M percentage, the regulator-focus metric */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-amber-50 rounded p-3 border border-amber-200">
          <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
            Bank — to {productLabel.toLowerCase()}s (GAR ≤ $1M)
          </p>
          <p className="text-2xl font-bold text-amber-900 tabular-nums mt-1">
            {bank.garLessThan1MPct != null ? `${(bank.garLessThan1MPct * 100).toFixed(1)}%` : '—'}
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            {bank.garLessThan1M ?? 0} of {total} loan{total === 1 ? '' : 's'}
          </p>
        </div>
        <div className="bg-blue-50 rounded p-3 border border-blue-200">
          <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">{aggregateLabel}</p>
          <p className="text-2xl font-bold text-blue-900 tabular-nums mt-1">
            {aggGar != null ? `${(aggGar * 100).toFixed(1)}%` : '—'}
          </p>
          <p className="text-xs text-blue-700 mt-0.5">FFIEC CRA aggregate</p>
        </div>
        <div className="bg-gray-50 rounded p-3 border border-gray-200">
          <p className="text-[10px] uppercase tracking-wide text-gray-600 font-semibold">D&amp;B {productLabel}</p>
          <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
            {dnbGar != null ? `${(dnbGar * 100).toFixed(1)}%` : '—'}
          </p>
          <p className="text-xs text-gray-700 mt-0.5">{productLabel} demographic</p>
        </div>
      </div>

      {/* Full BPD table — Gross Revenue × Loan Amount tier */}
      <div className="border border-gray-200 rounded overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Gross Revenue × Loan Amount ({productLabel})
        </div>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200 text-gray-700">
            <tr>
              <th className="text-left px-3 py-1.5 font-semibold">Gross Revenue</th>
              {tierKeys.map(k => (
                <th key={k} className="text-right px-3 py-1.5 font-semibold whitespace-nowrap">
                  {k}
                </th>
              ))}
              <th className="text-right px-3 py-1.5 font-semibold">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100 bg-amber-50/30">
              <td className="px-3 py-1.5 font-medium">$1 Million or Less</td>
              {tierKeys.map(k => (
                <td key={k} className="px-3 py-1.5 text-right tabular-nums">
                  {garLessThan1MRow[k] ?? 0}
                  <div className="text-[10px] text-gray-500">{fmt(pct(garLessThan1MRow[k] ?? 0))}</div>
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                {rowSum(garLessThan1MRow)}
                <div className="text-[10px] text-gray-500">{fmt(pct(rowSum(garLessThan1MRow)))}</div>
              </td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="px-3 py-1.5 font-medium">Greater than $1 Million / Unknown</td>
              {tierKeys.map(k => (
                <td key={k} className="px-3 py-1.5 text-right tabular-nums">
                  {garGreaterRow[k] ?? 0}
                  <div className="text-[10px] text-gray-500">{fmt(pct(garGreaterRow[k] ?? 0))}</div>
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                {rowSum(garGreaterRow)}
                <div className="text-[10px] text-gray-500">{fmt(pct(rowSum(garGreaterRow)))}</div>
              </td>
            </tr>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-3 py-1.5">TOTAL</td>
              {tierTotals.map((n, i) => (
                <td key={tierKeys[i]} className="px-3 py-1.5 text-right tabular-nums">
                  {n}
                  <div className="text-[10px] text-gray-500 font-normal">{fmt(pct(n))}</div>
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums">{grandTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
